import { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { ulid } from 'ulid';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { CampaignViewModel } from '@/graphql/generated';
import { extractCheckResultError } from '@/lib/checkResult';
import { AutocompleteCreateFormProps } from './autocomplete-create-form';

import { CampaignForm } from '../../pages/campaigns/campaign-form';
import {
	buildCreateCampaignInput,
	type CampaignFormSubmitData
} from '../../pages/campaigns/campaign-input';
import { CAMPAIGN_CREATE_MUTATION } from '../../pages/campaigns/campaigns.gql';

export function CampaignCreateForm({
	onSuccess,
	onCancel
}: AutocompleteCreateFormProps<CampaignViewModel>) {
	const [submissionError, setSubmissionError] = useState<string | null>(null);
	const idempotentCampaignId = useMemo(() => ulid(), []);

	const [createCampaign, { loading: isSubmitting }] = useMutation(
		CAMPAIGN_CREATE_MUTATION,
		{
			// Prevent cache interactions that might trigger refreshes
			fetchPolicy: 'no-cache'
		}
	);

	const handleSubmit = async (formData: CampaignFormSubmitData) => {
		setSubmissionError(null);

		const payload = buildCreateCampaignInput(
			formData,
			idempotentCampaignId
		);

		try {
			const { data } = await createCampaign({
				variables: { input: payload }
			});

			const message = extractCheckResultError(data?.createCampaign);

			if (message) {
				setSubmissionError(message);
				return;
			}

			// Skip success toast in autocomplete context to prevent navigation issues
			// The autocomplete selection will provide user feedback

			// Create a minimal CampaignViewModel object to pass back
			// Note: facility and other fields will be populated when the list refreshes
			const newCampaign = {
				campaignId: idempotentCampaignId,
				name: formData.name,
				description: formData.description,
				startDate: formData.startDate,
				endDate: formData.endDate,
				isActive: formData.isActive
			} as CampaignViewModel;

			onSuccess(newCampaign);
		} catch (error) {
			const fallbackMessage =
				error instanceof Error
					? error.message
					: 'Unexpected error while creating campaign.';
			setSubmissionError(fallbackMessage);
		}
	};

	return (
		<div className="space-y-4">
			{submissionError && (
				<Alert variant="destructive">
					<AlertTitle>Unable to create campaign</AlertTitle>
					<AlertDescription>{submissionError}</AlertDescription>
				</Alert>
			)}

			<CampaignForm
				mode="create"
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				headerTitle="Create campaign"
				onBack={onCancel}
			/>
		</div>
	);
}
